import type { AiCapabilityPage } from "../schema.js";
import { LIVE, OWNER_ONLY } from "./_shared.js";

/**
 * `GRP-MEMBER-ROLE` (@UC0216) — trang **redirect** (US-AIV2-0049): chat KHÔNG hỏi ô, KHÔNG có nút Đồng ý,
 * chỉ một câu giải thích + nút mở đúng màn hình `/groups/:groupId/members` (ADR-0009 bất biến 5) — bấm một người ⇒ "Đổi vai trò"
 * (QĐ người dùng 12/10/2026 bật lại nút ở Chi tiết thành viên; trước đó trang trỏ màn quản trị viên CHỈ ĐỌC và nói "chưa làm được").
 * Cửa mobile trang này chịu trách nhiệm (đổi vai là đổi QUYỀN CHẠM TIỀN ⇒ bấm tận tay trên màn danh sách).

 * 🔴 Vai HẸP HƠN cảm giác ban đầu (US-AIV2-0049, review #827 H1 — ĐO từ guard thật, đừng nới lại):
 *   `POST /groups/{groupId}/members/{memberId}/role` gác `@RequireGroupRoles(OWNER)` — đổi vai là đổi QUYỀN CHẠM TIỀN, chỉ chủ nhóm.
 *   Trang redirect chỉ mở màn hình, nên mời sai vai = dẫn người ta tới nút bấm vào là 403
 *   (CLAUDE.md §4 cấm: "dẫn member thường vào form quản trị rồi để 403 ở bước chốt").
 *   Khoá bằng `roles-parity.invariants.spec.ts` — nới ở đây mà GUI không nới thì test ĐỎ.
 */
export const GRP_MEMBER_ROLE = {
  id: "GRP-MEMBER-ROLE",
  uc: "@UC0216",
  tier: "redirect",
  status: LIVE,
  roles: OWNER_ONLY,
  flags: [],
  slots: [],
  llm: {
    description:
      "ĐỔI vai của một người trong nhóm: cho ai đó làm quản trị, làm thủ quỹ, hay hạ về thành viên thường. Trang này GHI (thay quyền của một người), nên câu phải nêu một THAY ĐỔI. Câu chỉ HỎI ai đang giữ vai gì (\"ai là thủ quỹ\", \"ai đang làm quản trị nhóm\" / \"ai dang lam quan tri nhom\", \"xem ai giữ vai gì\") là ĐỌC và thuộc READ-PEOPLE-MINE — có chữ 'quản trị'/'thủ quỹ' KHÔNG đủ để vào đây.",
    examples: [
      "đổi vai thành viên",
      "doi vai thanh vien",
      "cho bạn ấy làm thủ quỹ",
      "cho ban ay lam thu quy",
      "gỡ quyền quản trị",
      // 🔴 US-AIV2-0101 — "ai làm thủ quỹ" ĐÃ GỠ. Nó là câu HỎI (đọc), còn trang này ĐỔI vai (ghi),
      // và nó khác đúng một chữ với ca `READ-PEOPLE-MINE#13` ("ai là thủ quỹ") ⇒ Sổ đang DẠY model
      // chọn trang ghi cho một câu đọc. Đo được: model chọn GRP-MEMBER-ROLE cho câu đọc ấy.
      // Câu đọc thuộc READ-PEOPLE-MINE ("ai giữ vai gì"), đã có ví dụ ở đó.
      "đặt bạn ấy làm quản trị",
      // ——— 0105: thêm cho đủ 40 ———
      "dat ban ay lam quan tri",
      "go quyen quan tri",
      "đổi vai cho Minh",
      "doi vai cho minh",
      "cho Lan làm thủ quỹ nhóm",
      "cho lan lam thu quy nhom",
      "phân quyền thành viên",
      "phan quyen thanh vien",
      "nâng Hùng lên quản trị",
      "nang hung len quan tri",
      "hạ bạn ấy xuống thành viên thường",
      "ha ban ay xuong thanh vien thuong",
      "bỏ vai thủ quỹ của An",
      "bo vai thu quy cua an",
      "tôi muốn đổi vai một người trong nhóm",
      "toi muon doi vai mot nguoi trong nhom",
      "thêm một quản trị nữa cho nhóm",
      "them mot quan tri nua cho nhom",
      "đổi thủ quỹ nhóm sang người khác",
      "doi thu quy nhom sang nguoi khac",
      "set quyền admin cho bạn đó",
      "set quyen admin cho ban do",
      "gán vai thủ quỹ",
      "gan vai thu quy",
      "đổi quyền của thành viên kiểu gì?",
      "doi quyen cua thanh vien kieu gi",
      "cho bạn ấy quyền quản lý quỹ",
      "cho ban ay quyen quan ly quy",
      "rút quyền của quản trị cũ",
      "rut quyen cua quan tri cu",
      "đổi vai",
      "phan vai trong nhom",
      "phân vai trong nhóm",
      "cất chức thủ quỹ cũ đi",
      // 🔴 US-AIV2-0112 lô Sổ 2 (NEED C08 #1017 · @UC0216) — nhóm lớp gọi phụ huynh theo TÊN CON ("mẹ bé X"). Thiếu cách nói đó, BM25 xếp
      // PAY-ON-BEHALF/INC-RECORD (chữ "mẹ bé") trên trang này ⇒ thủ quỹ gõ "cho mẹ bé Anh Thư làm thủ quỹ" nhận câu chung "chưa rõ ý" thay
      // vì câu khoá C08 "việc này do chủ nhóm làm" (`lockedPageHint` đòi trang khoá vượt trang được phép ≥120%: trước 4,89 vs 10,57; sau
      // 12,97 vs 10,30 — đo offline). Tên bé (Bin, Na) là từ KHÔNG có trong câu nào của golden-set ⇒ không đổi IDF của trang khác; KHÁC
      // câu probe ("Anh Thư") để số đo không tự trả lời chính nó.
      "cho mẹ bé Bin làm thủ quỹ",
      "cho me be bin lam thu quy",
      "cho mẹ bé Na làm thủ quỹ",
      "cho me be na lam thu quy",
    ],
  },
  screen: "/groups/:groupId/members",
  screenShows: "member_role_change",
  screenLabel: "Xem thành viên",
  endpoints: [
    "POST /groups/{groupId}/members/{memberId}/role",
  ],
  facts: [],
  templates: {
    // QĐ người dùng 12/10/2026 — nút «Đổi vai trò» BẬT LẠI ở Chi tiết thành viên (chỉ chủ nhóm; Quản trị viên / Thành viên — thủ quỹ tạm
    // ẩn). Câu cũ ("hiện chưa làm được") nay là nói sai ⇒ chỉ đúng chỗ bấm.
    // AI-SO-DRIFT-1012 (@UC0216): vai Thủ quỹ ẨN khỏi ô chọn (`assignable_group_role.dart`) ⇒ "cho Lan làm thủ quỹ" phải nghe thẳng là chưa gán được.
    redirect:
      "Đổi vai làm ở màn Thành viên: bấm vào người cần đổi rồi chọn «Đổi vai trò» (chỉ chủ nhóm, đổi thành Quản trị viên hoặc Thành viên — vai Thủ quỹ hiện chưa gán được). Mình mở danh sách thành viên cho bạn nhé.",
  },
  journey: { after: ["READ-PEOPLE-MINE"] },
  help: { title: "Đổi vai thành viên", tryNow: "đổi vai thành viên" },
} satisfies AiCapabilityPage;
